import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, MapPin } from "lucide-react";
import SectionLabel from "./brand/SectionLabel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Wireless PtP / PtMP Link",
  "Home or Business Networking",
  "Cell Booster (Home / Small Business)",
  "Small or Medium Self-Supporting Tower",
  "DAS System (Commercial / Enterprise)",
  "Solar / Farm / Acreage Comms",
  "Other — I'll explain in the message",
];

export default function ContactForm({ source = "epicom-systems" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: SERVICE_OPTIONS[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email, and project details.");
      return;
    }
    if (form.message.trim().length < 5) {
      toast.error("Message should be at least 5 characters.");
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, { ...form, source });
      setSubmitted(true);
      toast.success(data.message || "Thanks — we'll be in touch.");
      setForm({
        name: "",
        email: "",
        phone: "",
        service_type: SERVICE_OPTIONS[0],
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Something went wrong. Please email rod@epicom.ca directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-bg-primary border-t border-steel-dark overflow-hidden"
    >
      <div className="absolute inset-0 bg-lattice-grid bg-lattice opacity-20 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <SectionLabel className="mb-5">Make Contact</SectionLabel>
            <h2 className="heading text-4xl md:text-5xl lg:text-6xl text-white uppercase leading-[0.95] mb-8">
              Request a <br />
              <span className="text-signal">Quote.</span>
            </h2>
            <p className="text-steel-light text-base md:text-lg leading-relaxed mb-10">
              Tell us about your site, project, or problem. We'll respond
              within two business days with a clear next step — a phone call, a
              site-survey booking, or a scoped quote.
            </p>

            <div className="space-y-4">
              <a
                href="mailto:rod@epicom.ca"
                data-testid="contact-email-link"
                className="flex items-center gap-4 group border border-steel-dark hover:border-signal p-5 transition-colors"
              >
                <Mail className="text-signal" size={22} />
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] text-steel-light uppercase">
                    Email
                  </div>
                  <div className="text-white group-hover:text-signal transition-colors font-medium">
                    rod@epicom.ca
                  </div>
                </div>
              </a>
              <div className="flex items-center gap-4 border border-steel-dark p-5">
                <MapPin className="text-signal" size={22} />
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] text-steel-light uppercase">
                    Based In
                  </div>
                  <div className="text-white font-medium">
                    Southern Alberta · Will Travel
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            {submitted ? (
              <div
                data-testid="contact-success-state"
                className="border border-signal bg-bg-secondary p-10 md:p-14 text-center"
              >
                <SectionLabel className="mb-4">Transmission Received</SectionLabel>
                <h3 className="heading text-3xl md:text-4xl text-white uppercase mb-4">
                  We Got It.
                </h3>
                <p className="text-steel-light mb-8">
                  Your request is in. Rod will reach out shortly. If it's
                  urgent, give us a direct shout at{" "}
                  <a href="mailto:rod@epicom.ca" className="text-signal underline">
                    rod@epicom.ca
                  </a>
                  .
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-send-another"
                  className="font-mono text-xs tracking-[0.2em] uppercase text-steel-light hover:text-signal border border-steel-dark hover:border-signal px-6 py-3 transition-colors"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="border border-steel-dark bg-bg-secondary p-8 md:p-10 space-y-6"
                noValidate
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.25em] text-steel-light uppercase block mb-2">
                      Name *
                    </label>
                    <input
                      data-testid="contact-input-name"
                      type="text"
                      required
                      value={form.name}
                      onChange={set("name")}
                      className="w-full bg-bg-primary border border-steel-dark text-white p-4 focus:border-signal focus:outline-none focus:ring-1 focus:ring-signal transition-colors"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.25em] text-steel-light uppercase block mb-2">
                      Email *
                    </label>
                    <input
                      data-testid="contact-input-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      className="w-full bg-bg-primary border border-steel-dark text-white p-4 focus:border-signal focus:outline-none focus:ring-1 focus:ring-signal transition-colors"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.25em] text-steel-light uppercase block mb-2">
                      Phone
                    </label>
                    <input
                      data-testid="contact-input-phone"
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      className="w-full bg-bg-primary border border-steel-dark text-white p-4 focus:border-signal focus:outline-none focus:ring-1 focus:ring-signal transition-colors"
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.25em] text-steel-light uppercase block mb-2">
                      Service *
                    </label>
                    <select
                      data-testid="contact-input-service"
                      value={form.service_type}
                      onChange={set("service_type")}
                      className="w-full bg-bg-primary border border-steel-dark text-white p-4 focus:border-signal focus:outline-none focus:ring-1 focus:ring-signal transition-colors appearance-none"
                      style={{
                        backgroundImage:
                          "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23E84A1A%22 stroke-width=%222%22><polyline points=%226 9 12 15 18 9%22/></svg>')",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 1rem center",
                        paddingRight: "2.5rem",
                      }}
                    >
                      {SERVICE_OPTIONS.map((o) => (
                        <option key={o} value={o} className="bg-bg-primary">
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="font-mono text-[10px] tracking-[0.25em] text-steel-light uppercase block mb-2">
                    Project Details *
                  </label>
                  <textarea
                    data-testid="contact-input-message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={set("message")}
                    className="w-full bg-bg-primary border border-steel-dark text-white p-4 focus:border-signal focus:outline-none focus:ring-1 focus:ring-signal transition-colors resize-none"
                    placeholder="Location, scope, timeline, existing equipment, signal issues — anything helpful."
                  />
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                  <p className="font-mono text-[10px] tracking-[0.2em] text-steel-light uppercase">
                    We reply within 2 business days.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="group inline-flex items-center justify-center gap-3 bg-signal hover:bg-signal-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wider px-8 py-4 border-2 border-transparent hover:border-white/20 transition-all"
                  >
                    {submitting ? "Transmitting…" : "Send Request"}
                    {!submitting && (
                      <Send
                        size={16}
                        className="group-hover:translate-x-1 transition-transform"
                      />
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
